import * as constants from '../constants/activityConstant';
import { ActivityItem } from '@/types/activity';

interface ActivityState {
  mergedActivityByLead: Record<number, ActivityItem[]>; // Merged & sorted
  loadingByLead: Record<number, boolean>;
  error: string | null;
}

const initialState: ActivityState = {
  mergedActivityByLead: {},
  loadingByLead: {},
  error: null
};

/**
 * Merge history and emails into unified timeline
 * Sorted by created_at/date descending (newest first)
 */
function mergeAndSortActivity(history: any[], emails: any[]): ActivityItem[] {
  // Transform history events
  const historyItems: ActivityItem[] = history.map(event => ({
    ...event,
    type: 'history' as const,
    created_at: event.created_at.replace(' ', 'T') + '.000Z' //convert to ISO 8601 format
  }));


  // Transform emails
  const emailItems: ActivityItem[] = emails.map(msg => ({
    ...msg,
    type: 'email' as const,
    message_id: msg.id,
    created_at: msg.createdAt // Use createdAt as created_at for sorting
  }));

  // Merge and sort by timestamp (newest first)
  const merged = [...historyItems, ...emailItems].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA; // Descending
  });

  return merged;
}

const activityReducer = (state = initialState, action: any): ActivityState => {
  switch (action.type) {
    case constants.FETCH_LEAD_ACTIVITY_REQUEST:
      const requestLeadId = action.params.leadId;
      return {
        ...state,
        loadingByLead: {
          ...state.loadingByLead,
          [requestLeadId]: true
        },
        error: null
      };

    case constants.FETCH_LEAD_ACTIVITY_SUCCESS:
      const { leadId, history, emails } = action.data;
      const mergedActivity = mergeAndSortActivity(history, emails);
      
      return {
        ...state,
        loadingByLead: {
          ...state.loadingByLead,
          [leadId]: false
        },
        mergedActivityByLead: {
          ...state.mergedActivityByLead,
          [leadId]: mergedActivity
        }
      };

    case constants.FETCH_LEAD_ACTIVITY_FAILURE:
      const failureLeadId = action.data?.leadId;
      return {
        ...state,
        loadingByLead: {
          ...state.loadingByLead,
          ...(failureLeadId ? { [failureLeadId]: false } : {})
        },
        error: action.error
      };

    case constants.UPDATE_EMAIL_READ_STATUS:
      const { leadId: lid, messageId, isRead } = action.data;
      const activity = state.mergedActivityByLead[lid] || [];
      
      return {
        ...state,
        mergedActivityByLead: {
          ...state.mergedActivityByLead,
          [lid]: activity.map(item => {
            if (item.type === 'email' && item.message_id === messageId) {
              return { ...item, isRead: isRead ? 1 : 0 };
            }
            return item;
          })
        }
      };

    case constants.CLEAR_ACTIVITY_STATE:
      return initialState;

    default:
      return state;
  }
};

export default activityReducer;
